/**
 * Публічна сторінка аудиту — доступ по токену без логіну.
 * Маршрутизується з App.jsx коли window.location.pathname починається з /audit/<token>.
 *
 * Read-only — без кнопок «Запустити», без видалення, без share.
 * Має кнопку «PDF» (генерується клієнтом).
 */

import { useState, useEffect } from "react";

const scoreColor = (v) => v >= 90 ? "#0cce6b" : v >= 70 ? "#a3cc34" : v >= 50 ? "#ffa400" : "#ff4e42";

const SEVERITY = {
  critical: { label: "Критично", color: "#ef4444", bg: "rgba(239,68,68,0.10)" },
  high:     { label: "Високо",   color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
  medium:   { label: "Середньо", color: "#eab308", bg: "rgba(234,179,8,0.10)" },
  low:      { label: "Низько",   color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  info:     { label: "Інфо",     color: "#6b7280", bg: "rgba(107,114,128,0.10)" },
};
const CATEGORY = {
  tech: "Технічний SEO",
  cwv: "Швидкість (CWV)",
  seo: "Семантика/Контент",
  backlinks: "Беклінки",
  gsc: "Search Console",
  ai_visibility: "AI Видимість",
};

function md2html(md) {
  if (!md) return "";
  let h = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*$)/gm, "<h3>$1</h3>")
    .replace(/^## (.*$)/gm, "<h2 style='font-size:18px;margin:10px 0 6px'>$1</h2>")
    .replace(/^# (.*$)/gm, "<h2 style='font-size:18px;margin:10px 0 6px'>$1</h2>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code style='background:#eef;padding:1px 4px;border-radius:3px'>$1</code>")
    .replace(/^- (.*$)/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>\s*)+/gs, "<ul style='padding-left:20px;margin:8px 0'>$&</ul>")
    .replace(/\n\n/g, "</p><p style='margin:8px 0'>");
  return "<p style='margin:8px 0'>" + h + "</p>";
}

// PDF builder — identical to AuditView.buildPdfHtml but standalone
function buildPdfHtml({ run, findings, project }) {
  const date = new Date(run.started_at).toLocaleDateString("uk-UA", { year: "numeric", month: "long", day: "numeric" });
  const sCol = scoreColor(run.score_overall);
  const cats = ["tech", "cwv", "seo", "backlinks", "gsc"];
  const aiCritical = run.ai_top_critical || [];
  const aiWins = run.ai_top_wins || [];
  const plan = run.ai_action_plan || { "30d": [], "60d": [], "90d": [] };
  const narrative = run.ai_narrative || {};
  const sevSort = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
  const sortedFindings = [...findings].sort((a, b) => (sevSort[a.severity] || 9) - (sevSort[b.severity] || 9));

  return `<!DOCTYPE html><html lang="uk"><head><meta charset="UTF-8"><title>SEO Audit 360 — ${project.project_name}</title>
<style>
@page{size:A4;margin:15mm 18mm}@media print{.page-break{page-break-before:always}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
*{box-sizing:border-box;margin:0;padding:0}body{font-family:Inter,Arial,sans-serif;font-size:11px;color:#1a1a2e;line-height:1.55}
.doc{max-width:780px;margin:0 auto}.cover{height:230mm;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;page-break-after:always}
.cover h1{font-size:42px;font-weight:800;margin-bottom:8px}.cover .domain{font-size:18px;color:#6366f1;margin-bottom:60px}
.cover .score-big{font-size:140px;font-weight:800;line-height:1;color:${sCol}}.cover .score-suffix{font-size:36px;color:#888}
.section{margin:24px 0}.section h2{font-size:20px;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.summary-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:14px 0}
.score-card{padding:12px;border-radius:8px;text-align:center;border:1px solid #e5e7eb}
.score-card .v{font-size:28px;font-weight:800}.score-card .l{font-size:10px;color:#888;margin-top:4px}
.finding{border-left:4px solid #6366f1;padding:8px 12px;margin:6px 0;background:#fafafa;border-radius:0 4px 4px 0}
.finding .ft{font-weight:700;font-size:12px}.finding .fd{font-size:11px;color:#555;margin:2px 0}
.finding .fr{font-size:11px;background:rgba(99,102,241,0.06);padding:4px 8px;border-radius:4px;margin-top:4px}
.sev-critical{border-color:#ef4444;background:rgba(239,68,68,0.04)}
.sev-high{border-color:#f59e0b;background:rgba(245,158,11,0.04)}
.sev-medium{border-color:#eab308;background:rgba(234,179,8,0.04)}
.sev-low{border-color:#3b82f6;background:rgba(59,130,246,0.04)}
.win-table{width:100%;border-collapse:collapse;margin-top:8px;font-size:10.5px}
.win-table th{background:#6366f1;color:#fff;padding:6px 8px;text-align:left}.win-table td{padding:6px 8px;border-bottom:1px solid #e5e7eb}
.plan-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.plan-col{padding:14px;border-radius:8px;background:rgba(99,102,241,0.04);border:1px solid rgba(99,102,241,0.15)}
</style></head><body><div class="doc">
<div class="cover">
  <h1>SEO Audit 360</h1><div class="domain">${project.project_name}</div>
  <div class="domain" style="font-size:14px;color:#888">${project.project_domain || ""}</div>
  <div style="margin-top:40px"><span class="score-big">${run.score_overall}</span><span class="score-suffix">/100</span></div>
  <div style="margin-top:60px;font-size:11px;color:#888">${date}</div>
</div>
<div class="section"><h2>Executive Summary</h2>${md2html(run.ai_summary_md || "")}</div>
<div class="section"><h3>Розбивка по категоріях</h3><div class="summary-grid">
  ${cats.map(c => `<div class="score-card"><div class="v" style="color:${scoreColor(run["score_" + c])}">${run["score_" + c]}</div><div class="l">${CATEGORY[c]}</div></div>`).join("")}
</div></div>
<div class="page-break"></div>
<div class="section"><h2>Топ критичних проблем</h2>
  ${aiCritical.length === 0 ? "<p style='color:#666'>Критичних проблем не виявлено.</p>" : aiCritical.map((c, i) => `
    <div class="finding sev-critical"><div class="ft">${i+1}. ${c.title || ""}</div><div class="fd">${c.description || ""}</div>${c.recommendation ? `<div class="fr"><strong>→</strong> ${c.recommendation}</div>` : ""}</div>`).join("")}
</div>
<div class="section"><h2>Quick Wins</h2>${aiWins.length === 0 ? "<p>Не виявлено.</p>" : `
  <table class="win-table"><thead><tr><th>Що зробити</th><th>Impact</th><th>Effort</th></tr></thead><tbody>
  ${aiWins.map(w => `<tr><td><strong>${w.title || ""}</strong><br/><span style="font-size:10px;color:#666">${w.recommendation || w.description || ""}</span></td><td>${w.impact || "—"}</td><td>${(w.effort || "medium").toUpperCase()}</td></tr>`).join("")}
  </tbody></table>`}</div>
<div class="page-break"></div>
<div class="section"><h2>План 30/60/90</h2><div class="plan-grid">
  ${["30d","60d","90d"].map(p => `<div class="plan-col"><h3 style="color:#6366f1;margin-bottom:6px">${p === "30d" ? "Перші 30 днів" : p === "60d" ? "31-60 днів" : "61-90 днів"}</h3><ul style="padding-left:18px">${(plan[p] || []).map(x => `<li>${x}</li>`).join("") || "<li><em>немає</em></li>"}</ul></div>`).join("")}
</div></div>
<div class="page-break"></div>
<div class="section"><h2>Всі знахідки (${findings.length})</h2>
  ${["critical","high","medium","low","info"].map(sev => {
    const sf = sortedFindings.filter(f => f.severity === sev); if (!sf.length) return "";
    return `<h3 style="color:${SEVERITY[sev].color};margin-top:14px">${SEVERITY[sev].label} — ${sf.length}</h3>
    ${sf.map(f => `<div class="finding sev-${sev}"><div class="ft">${f.title}${f.url ? ` <span style="font-weight:400;color:#888;font-size:10px">(${f.url})</span>` : ""}</div>${f.description ? `<div class="fd">${f.description}</div>` : ""}${f.recommendation ? `<div class="fr">→ ${f.recommendation}</div>` : ""}</div>`).join("")}`;
  }).join("")}
</div>
</div></body></html>`;
}

export default function PublicAuditView({ token }) {
  const [run, setRun] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("Токен не вказано"); setLoading(false); return; }
    fetch(`/api/public/audit/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); return; }
        setRun(data.run);
        setFindings(data.findings || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const generatePDF = () => {
    if (!run) return;
    const html = buildPdfHtml({ run, findings, project: { project_name: run.project_name, project_domain: run.project_domain } });
    const win = window.open("", "_blank");
    if (!win) { alert("Дозвольте pop-up для друку PDF"); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 700);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80, fontFamily: "Inter,Arial,sans-serif" }}>Завантаження…</div>;
  if (error) return <div style={{ textAlign: "center", padding: 80, color: "#ef4444", fontFamily: "Inter,Arial,sans-serif" }}>{error}</div>;
  if (!run) return null;

  const aiCritical = run.ai_top_critical || [];
  const aiWins = run.ai_top_wins || [];
  const aiPlan = run.ai_action_plan || { "30d": [], "60d": [], "90d": [] };
  const cats = ["tech", "cwv", "seo", "backlinks", "gsc"];

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fb", fontFamily: "Inter,Arial,sans-serif", padding: "20px 0" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 24, gap: 12 }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 28, margin: 0, color: "#1a1a2e" }}>SEO Audit 360</h1>
            <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>
              {run.project_name} · {run.project_domain || ""} · {new Date(run.started_at).toLocaleDateString("uk-UA")}
            </div>
          </div>
          <button onClick={generatePDF} style={{ padding: "8px 16px", borderRadius: 6, background: "#6366f1", color: "#fff", border: 0, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            ⬇ PDF
          </button>
        </div>

        {/* Hero score */}
        <div style={{ background: "#fff", padding: 28, borderRadius: 12, marginBottom: 16, display: "flex", gap: 24, alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 80, fontWeight: 800, color: scoreColor(run.score_overall), lineHeight: 1 }}>
              {run.score_overall}
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>Health Score / 100</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, flex: 1 }}>
            {cats.map(c => (
              <div key={c} style={{ padding: 12, borderRadius: 8, textAlign: "center", border: "1px solid #e5e7eb", background: "#fafafa" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: scoreColor(run["score_" + c]) }}>{run["score_" + c]}</div>
                <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{CATEGORY[c]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Executive summary */}
        {run.ai_summary_md && (
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 12, color: "#6366f1" }}>Executive Summary</h2>
            <div style={{ fontSize: 13, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: md2html(run.ai_summary_md) }} />
          </div>
        )}

        {/* Top critical */}
        {aiCritical.length > 0 && (
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 12, color: "#ef4444" }}>Топ критичних проблем</h2>
            {aiCritical.map((c, i) => (
              <div key={i} style={{ padding: 14, marginBottom: 10, background: "rgba(239,68,68,0.04)", borderLeft: "4px solid #ef4444", borderRadius: "0 8px 8px 0" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{i + 1}. {c.title}</div>
                {c.description && <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>{c.description}</div>}
                {c.recommendation && (
                  <div style={{ fontSize: 12, padding: "8px 12px", background: "rgba(99,102,241,0.06)", borderRadius: 6 }}>
                    <strong>Рекомендація:</strong> {c.recommendation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Quick wins */}
        {aiWins.length > 0 && (
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 12, color: "#0cce6b" }}>⚡ Quick Wins</h2>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f0f0f5" }}>
                  <th style={{ padding: 10, textAlign: "left" }}>Що зробити</th>
                  <th style={{ padding: 10, width: 70 }}>Impact</th>
                  <th style={{ padding: 10, width: 80 }}>Effort</th>
                </tr>
              </thead>
              <tbody>
                {aiWins.map((w, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f0f0f5" }}>
                    <td style={{ padding: 10 }}>
                      <strong>{w.title}</strong>
                      {w.recommendation && <div style={{ fontSize: 11, color: "#777", marginTop: 3 }}>{w.recommendation}</div>}
                    </td>
                    <td style={{ padding: 10, textAlign: "center", fontWeight: 700, color: scoreColor(w.impact || 0) }}>{w.impact || "—"}</td>
                    <td style={{ padding: 10, textAlign: "center", fontWeight: 700, fontSize: 11,
                      color: w.effort === "low" ? "#0cce6b" : w.effort === "high" ? "#ff4e42" : "#ffa400" }}>
                      {(w.effort || "medium").toUpperCase()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 30/60/90 plan */}
        {(aiPlan["30d"]?.length || aiPlan["60d"]?.length || aiPlan["90d"]?.length) ? (
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 12, color: "#a855f7" }}>📅 План 30/60/90 днів</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
              {["30d", "60d", "90d"].map(p => (
                <div key={p} style={{ padding: 16, background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.15)", borderRadius: 10 }}>
                  <h3 style={{ fontSize: 14, marginTop: 0, marginBottom: 8, color: "#6366f1" }}>
                    {p === "30d" ? "Перші 30 днів" : p === "60d" ? "31-60 днів" : "61-90 днів"}
                  </h3>
                  <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, lineHeight: 1.7 }}>
                    {(aiPlan[p] || []).map((x, i) => <li key={i} style={{ marginBottom: 4 }}>{x}</li>)}
                    {!(aiPlan[p]?.length) && <li style={{ color: "#888", fontStyle: "italic" }}>—</li>}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Findings */}
        <div style={{ background: "#fff", padding: 24, borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 12 }}>Всі знахідки ({findings.length})</h2>
          {["critical", "high", "medium", "low", "info"].map(sev => {
            const sf = findings.filter(f => f.severity === sev);
            if (!sf.length) return null;
            return (
              <div key={sev}>
                <h3 style={{ fontSize: 13, color: SEVERITY[sev].color, marginTop: 16, marginBottom: 8 }}>
                  {SEVERITY[sev].label} — {sf.length}
                </h3>
                {sf.map(f => (
                  <div key={f.id} style={{
                    padding: 12, marginBottom: 6, borderLeft: `4px solid ${SEVERITY[sev].color}`,
                    background: SEVERITY[sev].bg, borderRadius: "0 6px 6px 0",
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3 }}>
                      {f.title}
                      {f.url && <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>({f.url})</span>}
                    </div>
                    {f.description && <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>{f.description}</div>}
                    {f.recommendation && (
                      <div style={{ fontSize: 11, padding: "5px 10px", background: "rgba(99,102,241,0.06)", borderRadius: 4 }}>
                        → {f.recommendation}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", color: "#888", fontSize: 11, padding: 20 }}>
          Виконано через <strong style={{ color: "#6366f1" }}>Spalah</strong> by <strong>Svitlo Agency</strong>
        </div>
      </div>
    </div>
  );
}
